import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, type SportsHealthRow } from '@/services/adminApi';
import { SPORTS_SOURCE_NAMES, type SportsProviderName } from '@/types/sports';
import { formatRelativeTime } from '@/i18n/relativeTime';

type RowBusy = Record<SportsProviderName, boolean>;

const initialBusy: RowBusy = SPORTS_SOURCE_NAMES.reduce<RowBusy>((acc, name) => {
  acc[name] = false;
  return acc;
}, {} as RowBusy);

function StatusDot({ state }: { state: 'ok' | 'down' | 'unknown' }): JSX.Element {
  const { t } = useTranslation();
  const label = state === 'ok' ? t('admin.common.statusUp') : state === 'down' ? t('admin.common.statusDown') : t('admin.common.statusNotTested');
  return <span className={`health-dot health-dot--${state}`} aria-label={label} role="img" />;
}

export function SportsHealthPage(): JSX.Element {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Record<SportsProviderName, SportsHealthRow | null>>(() =>
    SPORTS_SOURCE_NAMES.reduce<Record<SportsProviderName, SportsHealthRow | null>>((acc, name) => {
      acc[name] = null;
      return acc;
    }, {} as Record<SportsProviderName, SportsHealthRow | null>),
  );
  const [busy, setBusy] = useState<RowBusy>(initialBusy);
  const [testAllBusy, setTestAllBusy] = useState(false);
  const [expanded, setExpanded] = useState<Set<SportsProviderName>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { checks } = await adminApi.listSportsHealthChecks();
      setRows((prev) => {
        const next = { ...prev };
        for (const check of checks) next[check.providerName] = check;
        return next;
      });
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t('admin.sportsHealth.loadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleTestOne(name: SportsProviderName): Promise<void> {
    setBusy((prev) => ({ ...prev, [name]: true }));
    try {
      const { check } = await adminApi.testSportsProvider(name);
      setRows((prev) => ({ ...prev, [name]: check }));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t('admin.sportsHealth.testError', { name }));
    } finally {
      setBusy((prev) => ({ ...prev, [name]: false }));
    }
  }

  async function handleTestAll(): Promise<void> {
    setTestAllBusy(true);
    try {
      const { checks } = await adminApi.testAllSportsProviders();
      setRows((prev) => {
        const next = { ...prev };
        for (const check of checks) next[check.providerName] = check;
        return next;
      });
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t('admin.sportsHealth.testAllError'));
    } finally {
      setTestAllBusy(false);
    }
  }

  function toggleExpand(name: SportsProviderName): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('admin.sportsHealth.title')}</h1>
        <p className="muted">
          {t('admin.sportsHealth.blurb')}
        </p>
      </header>

      <section className="admin-card">
        <div className="admin-card__row">
          <h2 className="admin-card__title">{t('admin.common.providers')}</h2>
          <button
            type="button"
            className="btn"
            onClick={() => void handleTestAll()}
            disabled={testAllBusy}
            aria-label={t('admin.sportsHealth.testAllAria')}
          >
            {testAllBusy ? t('admin.common.testing') : t('admin.common.testAll')}
          </button>
        </div>
        {loading && <p className="muted" role="status">{t('common.loading')}</p>}
        {loadError && <p className="error" role="alert">{loadError}</p>}
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col" aria-label={t('admin.common.status')} />
                <th scope="col">{t('admin.common.source')}</th>
                <th scope="col">{t('admin.common.lastTested')}</th>
                <th scope="col" className="num">{t('admin.common.latency')}</th>
                <th scope="col" aria-label={t('admin.common.actions')} />
              </tr>
            </thead>
            <tbody>
              {SPORTS_SOURCE_NAMES.map((name) => {
                const row = rows[name];
                const state: 'ok' | 'down' | 'unknown' = row === null ? 'unknown' : row.ok ? 'ok' : 'down';
                const isExpanded = expanded.has(name);
                const rowBusy = busy[name];
                return (
                  <Fragment key={name}>
                    <tr>
                      <td><StatusDot state={state} /></td>
                      <td><code>{name}</code></td>
                      <td>{row ? formatRelativeTime(t, row.testedAt, nowMs) : <span className="text-dim">{t('common.relativeTime.never')}</span>}</td>
                      <td className="num">
                        {row && row.ok && row.latencyMs !== null
                          ? `${row.latencyMs} ms`
                          : <span className="text-dim">—</span>}
                      </td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => void handleTestOne(name)}
                          disabled={rowBusy}
                          aria-label={t('admin.common.testProviderAria', { name })}
                        >
                          {rowBusy ? t('admin.common.testing') : t('admin.common.test')}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => toggleExpand(name)}
                          aria-expanded={isExpanded}
                          aria-controls={`sports-health-details-${name}`}
                          aria-label={isExpanded ? t('admin.common.hideDetails', { name }) : t('admin.common.showDetails', { name })}
                        >
                          {isExpanded ? '▾' : '▸'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr id={`sports-health-details-${name}`} className="admin-redemptions-row">
                        <td colSpan={5}>
                          {row === null && <p className="muted">{t('admin.common.noProbeYet')}</p>}
                          {row && row.ok && (
                            <p className="muted">{t('admin.sportsHealth.upSince', { date: new Date(row.testedAt).toLocaleString() })}</p>
                          )}
                          {row && !row.ok && (
                            <dl className="health-details">
                              <dt>{t('admin.common.kind')}</dt>
                              <dd><code>{row.kind ?? 'unknown'}</code></dd>
                              <dt>{t('admin.common.message')}</dt>
                              <dd>{row.message ?? <span className="text-dim">—</span>}</dd>
                              <dt>{t('admin.sportsHealth.testedAt')}</dt>
                              <dd>{new Date(row.testedAt).toLocaleString()}</dd>
                            </dl>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
